// @ts-ignore
/* eslint-disable */
import { request } from '@umijs/max';

/** 业务成功：仅判断 code === 1（data 可为 null，例如删除接口） */
export function isApiSuccess(res: unknown): boolean {
  const r = res as API.BackendResult | undefined;
  return r != null && Number(r.code) === 1;
}

/** 列表接口常见成功码（与写操作统一用 1 不同，部分项目列表用 200 / 0） */
const BACKEND_LIST_OK_CODES = new Set([1, 200, 0]);

/**
 * 从 Result 或裸数组中取出列表（兼容 data 为数组、data.list、data.records、顶层 list）
 */
export function extractBackendListData<T>(res: unknown): T[] {
  if (res == null) return [];
  if (Array.isArray(res)) return res as T[];
  const r = res as Record<string, unknown>;
  const data = r.data;
  if (Array.isArray(data)) return data as T[];
  if (data != null && typeof data === 'object') {
    const o = data as { list?: T[]; records?: T[]; data?: unknown };
    if (Array.isArray(o.list)) return o.list;
    if (Array.isArray(o.records)) return o.records;
    if (Array.isArray(o.data)) return o.data as T[];
  }
  if (Array.isArray(r.list)) return r.list as T[];
  return [];
}

/** 从后端分页结果中取 total，兼容 data.total / data.count / 顶层 total */
export function extractBackendListTotal(res: unknown): number | undefined {
  if (res == null || Array.isArray(res)) return undefined;
  const r = res as Record<string, unknown>;
  const toNum = (value: unknown): number | undefined => {
    if (value === undefined || value === null || value === '') return undefined;
    const n = Number(value);
    return Number.isFinite(n) ? n : undefined;
  };
  const data = r.data;
  if (data != null && typeof data === 'object') {
    const o = data as { total?: unknown; count?: unknown; totalCount?: unknown };
    return toNum(o.total) ?? toNum(o.count) ?? toNum(o.totalCount);
  }
  return toNum(r.total) ?? toNum(r.count) ?? toNum(r.totalCount);
}

/** 列表请求是否应视为成功（用于 ProTable success、是否弹错误） */
export function isBackendListRequestSuccess(res: unknown): boolean {
  if (res == null) return false;
  if (Array.isArray(res)) return true;
  const r = res as Record<string, unknown>;
  const code = r.code;
  if (code !== undefined && code !== null) {
    return BACKEND_LIST_OK_CODES.has(Number(code));
  }
  const data = r.data;
  if (Array.isArray(data)) return true;
  if (data != null && typeof data === 'object') {
    const o = data as { list?: unknown[]; records?: unknown[]; code?: unknown; data?: unknown };
    if (Array.isArray(o.list) || Array.isArray(o.records)) return true;
    if (o.code !== undefined && o.code !== null) {
      return BACKEND_LIST_OK_CODES.has(Number(o.code));
    }
    if (Array.isArray(o.data)) return true;
  }
  return Array.isArray(r.list);
}

/** 获取当前的用户 GET /api/currentUser */
export async function currentUser(options?: { [key: string]: any }) {
  return request<{
    data: API.CurrentUser;
  }>('/api/currentUser', {
    method: 'GET',
    ...(options || {}),
  });
}

/** 退出登录接口 POST /api/login/outLogin */
export async function outLogin(options?: { [key: string]: any }) {
  const { headers: optHeaders, data: optData, ...rest } = options || {};
  const token =
    typeof window !== 'undefined' ? localStorage.getItem('token') : undefined;
  return request<Record<string, any>>('/api/login/outLogin', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...optHeaders,
    },
    data: {
      ...(token ? { token } : {}),
      ...optData,
    },
    ...rest,
  });
}

/** 登录接口 POST /accounts/login */
export async function login(body: API.LoginParams, options?: { [key: string]: any }) {
  return request<API.BackendResult<API.AccountLoginVO>>('/accounts/login', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

/** 用户列表 GET /users/list */
export async function queryUsers(
  params: {
    pageNum?: number;
    pageSize?: number;
    productId?: number;
    userIdentity?: number;
    name?: string;
  },
  options?: { [key: string]: any },
) {
  return request<API.BackendResult<API.ScoutechUser[]>>('/users/list', {
    method: 'GET',
    params,
    ...(options || {}),
  });
}

/** 产品列表 GET /products/list */
export async function queryProducts(
  params?: {
    pageNum?: number;
    pageSize?: number;
    productName?: string;
  },
  options?: { [key: string]: any },
) {
  return request<API.BackendResult<API.ScoutechProduct[]>>('/products/list', {
    method: 'GET',
    params: params || {},
    ...(options || {}),
  });
}

/** 产品详情 GET /products/:id */
export async function getProduct(id: number, options?: { [key: string]: any }) {
  return request<API.BackendResult<API.ScoutechProduct>>(`/products/${id}`, {
    method: 'GET',
    ...(options || {}),
  });
}

/** 新建产品 POST /products */
export async function createProduct(
  body: Pick<API.ScoutechProduct, 'productName'>,
  options?: { [key: string]: any },
) {
  return request<API.BackendResult<API.ScoutechProduct>>('/products', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

/** 更新产品 PUT /products/:id */
export async function updateProduct(
  id: number,
  body: Pick<API.ScoutechProduct, 'productName'>,
  options?: { [key: string]: any },
) {
  return request<API.BackendResult<unknown>>(`/products/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

/** 删除产品 DELETE /products/:id */
export async function deleteProduct(id: number, options?: { [key: string]: any }) {
  return request<API.BackendResult<unknown>>(`/products/${id}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

/** 用户详情 GET /users/:id */
export async function getUser(id: number, options?: { [key: string]: any }) {
  return request<API.BackendResult<API.ScoutechUser>>(`/users/${id}`, {
    method: 'GET',
    ...(options || {}),
  });
}

/** 新建用户 POST /users */
export async function createUser(body: API.ScoutechUser, options?: { [key: string]: any }) {
  return request<API.BackendResult<API.ScoutechUser>>('/users', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

/** 更新用户 PUT /users/:id */
export async function updateUser(
  id: number,
  body: API.ScoutechUser,
  options?: { [key: string]: any },
) {
  return request<API.BackendResult<unknown>>(`/users/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

/** 删除用户 DELETE /users/:id */
export async function deleteUser(id: number, options?: { [key: string]: any }) {
  return request<API.BackendResult<unknown>>(`/users/${id}`, {
    method: 'DELETE',
    ...(options || {}),
  });
}

/** 此处后端没有提供注释 GET /api/notices */
export async function getNotices(options?: { [key: string]: any }) {
  return request<API.NoticeIconList>('/api/notices', {
    method: 'GET',
    ...(options || {}),
  });
}

